/*
 * Entry point of the application.
 * Only platform bootstrapping code should be here.
 * For app-specific initialization, use `app/app.component.ts`.
 */

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { RouteReusableStrategy } from '@app/core';
import { EncodeUrlParametersInterceptor } from '@app/core/interceptor/encode-url-parameters.interceptor';
import { environment } from '@env/environment';
import 'hammerjs';
import { enableElfProdMode } from '@ngneat/elf';
import { APP_ROUTES } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { GlobalErrorHandler } from './app/core/error-handler/globalErrorHandler';
import { ProdGuard } from './app/core/guard/prod.guard';
import { RightGuard } from './app/core/guard/right.guard';
import { WartungsdienstNotificationGuard } from './app/core/guard/wartungsdienst-notification.guard';
import { CommonErrorInterceptor } from './app/core/interceptor/common.error.interceptor';
import { HttpCancelInterceptor } from './app/core/interceptor/http.cancel.interceptor';
import { CsvService } from './app/core/services/csv.service';
import { DeviceService } from './app/core/services/device.service';
import { ExpandedPanelHistoryService } from './app/core/services/expanded-panel-history.service';
import { FileDownloadService } from './app/core/services/file-download.service';
import { FooterVersionSelectService } from './app/core/services/footer-version-select.service';
import { HttpCancelService } from './app/core/services/http.cancel.service';
import { LoaderService } from './app/core/services/loader.service';
import { RouterService } from './app/core/services/router.service';
import { SwUpdateService } from './app/core/services/sw.update.service';
import { CompareUtil } from './app/core/util/compare.util';
import { FilterUtil } from './app/core/util/filter.util';
import { HttpUtil } from './app/core/util/http.util';
import { PayloadUtil } from './app/core/util/payload.util';
import { RightUtil } from './app/core/util/right.util';
import { StaticPathUtil } from './app/core/util/static-path.util';
import { StringUtil } from './app/core/util/string.util';

if (environment.production) {
  enableProdMode();
  enableElfProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    CsvService,
    RouterService,
    DeviceService,
    LoaderService,
    FooterVersionSelectService,
    ExpandedPanelHistoryService,
    StringUtil,
    PayloadUtil,
    StaticPathUtil,
    FilterUtil,
    SwUpdateService,
    RightUtil,
    ProdGuard,
    RightGuard,
    WartungsdienstNotificationGuard,
    HttpCancelService,
    HttpUtil,
    FileDownloadService,
    CompareUtil,
    {
      // processes all errors
      provide:  ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide:  RouteReuseStrategy,
      useClass: RouteReusableStrategy
    },
    {
      provide:  HTTP_INTERCEPTORS,
      useClass: CommonErrorInterceptor,
      multi:    true
    },
    {
      provide:  HTTP_INTERCEPTORS,
      useClass: HttpCancelInterceptor,
      multi:    true
    },
    {
      provide:  HTTP_INTERCEPTORS,
      useClass: EncodeUrlParametersInterceptor,
      multi:    true
    },
    {
      provide:  MAT_DATE_LOCALE,
      useValue: 'de-AT'
    },
    {
      provide:  MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {duration: 4000}
    },
    {
      provide:  MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {panelClass: 'dialog-default'}
    },
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(BrowserModule, ServiceWorkerModule.register('./ngsw-worker.js', {
        enabled:              environment.serviceWorker,
        registrationStrategy: 'registerImmediately'
      }), MatNativeDateModule
    ),
    provideAnimations(),
    provideRouter(APP_ROUTES, withComponentInputBinding())
  ]
})
  .catch((err) => console.error(err));
